import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Webhook,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Zap,
  ShoppingCart,
  Package,
  CreditCard,
  User,
  Bell,
  History,
  Settings,
  Play,
  Pause,
  ExternalLink,
  Copy,
  Eye,
  Clock
} from "lucide-react";

const API = "/api";

const WEBHOOK_TOPICS = [
  { value: "orders/create", label: "Sipariş Oluşturuldu", icon: ShoppingCart, category: "Siparişler" },
  { value: "orders/updated", label: "Sipariş Güncellendi", icon: ShoppingCart, category: "Siparişler" },
  { value: "orders/paid", label: "Sipariş Ödendi", icon: CreditCard, category: "Siparişler" },
  { value: "orders/cancelled", label: "Sipariş İptal Edildi", icon: XCircle, category: "Siparişler" },
  { value: "orders/fulfilled", label: "Sipariş Kargolandı", icon: Package, category: "Siparişler" },
  { value: "products/create", label: "Ürün Oluşturuldu", icon: Package, category: "Ürünler" },
  { value: "products/update", label: "Ürün Güncellendi", icon: Package, category: "Ürünler" },
  { value: "products/delete", label: "Ürün Silindi", icon: Trash2, category: "Ürünler" },
  { value: "inventory_levels/update", label: "Stok Güncellendi", icon: Package, category: "Stok" },
  { value: "customers/create", label: "Müşteri Oluşturuldu", icon: User, category: "Müşteriler" },
  { value: "refunds/create", label: "İade Oluşturuldu", icon: CreditCard, category: "İadeler" },
];

export default function WebhooksPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Webhooks
  const [webhooks, setWebhooks] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Form
  const [webhookForm, setWebhookForm] = useState({
    topic: "",
    enabled: true,
    autoSync: true,
    notifyEmail: false,
    email: ""
  });
  
  // Settings
  const [settings, setSettings] = useState({
    webhookUrl: "",
    secretKey: "",
    autoSyncOrders: true,
    autoSyncProducts: true,
    autoSyncInventory: true,
    retryFailedWebhooks: true,
    maxRetries: 3
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [webhooksRes, logsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/webhooks?token=${token}`),
        axios.get(`${API}/webhooks/logs?token=${token}&limit=50`),
        axios.get(`${API}/webhooks/settings?token=${token}`)
      ]);
      
      setWebhooks(webhooksRes.data.webhooks || []);
      setWebhookLogs(logsRes.data.logs || []);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error("Webhooks fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async () => {
    if (!webhookForm.topic) {
      toast.error("Webhook türü seçmelisiniz");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/webhooks?token=${token}`, webhookForm);
      toast.success("Webhook oluşturuldu");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Webhook oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  const deleteWebhook = async (webhookId) => {
    if (!confirm("Bu webhook'u silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/webhooks/${webhookId}?token=${token}`);
      toast.success("Webhook silindi");
      fetchData();
    } catch (error) {
      toast.error("Webhook silinemedi");
    }
  };

  const toggleWebhook = async (webhookId, enabled) => {
    try {
      await axios.put(`${API}/webhooks/${webhookId}/toggle?token=${token}`, { enabled });
      toast.success(enabled ? "Webhook aktifleştirildi" : "Webhook devre dışı bırakıldı");
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/webhooks/settings?token=${token}`, settings);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const retryWebhook = async (logId) => {
    try {
      await axios.post(`${API}/webhooks/logs/${logId}/retry?token=${token}`);
      toast.success("Webhook yeniden gönderildi");
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(settings.webhookUrl || window.location.origin + "/api/webhooks/incoming");
    toast.success("URL kopyalandı");
  };

  const viewLog = (log) => {
    setSelectedLog(log);
    setLogDialogOpen(true);
  };

  const getTopicInfo = (topic) => {
    return WEBHOOK_TOPICS.find(t => t.value === topic) || { label: topic, icon: Webhook };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return <Badge className="bg-emerald-500"><CheckCircle className="w-3 h-3 mr-1" />Başarılı</Badge>;
      case "failed":
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Başarısız</Badge>;
      case "pending":
        return <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" />Bekliyor</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="webhooks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Webhooks & Otomasyon</h1>
          <p className="text-slate-500 mt-1">Gerçek zamanlı Shopify bildirimleri ve otomatik senkronizasyon</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Webhook
        </Button>
      </div>

      {/* Webhook URL Info */}
      <Alert className="bg-blue-50 border-blue-200">
        <Webhook className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Webhook Alıcı URL'niz:</p>
              <code className="text-sm bg-blue-100 px-2 py-0.5 rounded">
                {settings.webhookUrl || `${window.location.origin}/api/webhooks/incoming`}
              </code>
            </div>
            <Button size="sm" variant="outline" onClick={copyWebhookUrl}>
              <Copy className="w-4 h-4 mr-1" />
              Kopyala
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="webhooks" className="space-y-6">
        <TabsList>
          <TabsTrigger value="webhooks" className="flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            Webhooklar
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Loglar
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Ayarlar
          </TabsTrigger>
        </TabsList>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Webhook className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{webhooks.length}</p>
                    <p className="text-xs text-slate-500">Toplam Webhook</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{webhooks.filter(w => w.enabled).length}</p>
                    <p className="text-xs text-slate-500">Aktif</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{webhookLogs.filter(l => l.status === "failed").length}</p>
                    <p className="text-xs text-slate-500">Başarısız (24s)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{webhookLogs.filter(l => l.status === "success").length}</p>
                    <p className="text-xs text-slate-500">İşlenen (24s)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Webhooks Table */}
          <Card>
            <CardHeader>
              <CardTitle>Aktif Webhooklar</CardTitle>
              <CardDescription>Shopify mağazanızdan gelen bildirimleri yönetin</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : webhooks.length === 0 ? (
                <div className="text-center py-12">
                  <Webhook className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz webhook oluşturulmadı</p>
                  <Button onClick={() => setDialogOpen(true)} className="mt-4" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Webhook'u Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Webhook Türü</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-center">Oto-Sync</TableHead>
                      <TableHead className="text-center">Durum</TableHead>
                      <TableHead className="text-center">Son İşlem</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((webhook) => {
                      const topicInfo = getTopicInfo(webhook.topic);
                      const TopicIcon = topicInfo.icon;
                      return (
                        <TableRow key={webhook.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <TopicIcon className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{topicInfo.label}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{topicInfo.category || "Genel"}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {webhook.autoSync ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                            ) : (
                              <XCircle className="w-5 h-5 text-slate-300 mx-auto" />
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={webhook.enabled}
                              onCheckedChange={(checked) => toggleWebhook(webhook.id, checked)}
                            />
                          </TableCell>
                          <TableCell className="text-center text-sm text-slate-500">
                            {webhook.lastTriggered 
                              ? new Date(webhook.lastTriggered).toLocaleString("tr-TR")
                              : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="icon" variant="ghost" onClick={() => deleteWebhook(webhook.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Webhook Logları
              </CardTitle>
              <CardDescription>Son 24 saatteki webhook aktiviteleri</CardDescription>
            </CardHeader>
            <CardContent>
              {webhookLogs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz log kaydı yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Webhook</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Mesaj</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhookLogs.map((log) => {
                      const topicInfo = getTopicInfo(log.topic);
                      return (
                        <TableRow key={log.id}>
                          <TableCell>
                            <span className="font-medium">{topicInfo.label}</span>
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm text-slate-500">
                            {log.message || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(log.created_at).toLocaleString("tr-TR")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="icon" variant="ghost" onClick={() => viewLog(log)}>
                                <Eye className="w-4 h-4 text-blue-500" />
                              </Button>
                              {log.status === "failed" && (
                                <Button size="icon" variant="ghost" onClick={() => retryWebhook(log.id)}>
                                  <RefreshCw className="w-4 h-4 text-amber-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Ayarları</CardTitle>
              <CardDescription>Otomatik senkronizasyon ve bildirim ayarlarını yapılandırın</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Siparişleri Otomatik Senkronize Et</Label>
                    <p className="text-xs text-slate-500">Yeni siparişleri Nebim'e aktar</p>
                  </div>
                  <Switch
                    checked={settings.autoSyncOrders}
                    onCheckedChange={(v) => setSettings({...settings, autoSyncOrders: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Ürünleri Otomatik Senkronize Et</Label>
                    <p className="text-xs text-slate-500">Ürün değişikliklerini Nebim'e aktar</p>
                  </div>
                  <Switch
                    checked={settings.autoSyncProducts}
                    onCheckedChange={(v) => setSettings({...settings, autoSyncProducts: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Stok Güncellemelerini Senkronize Et</Label>
                    <p className="text-xs text-slate-500">Stok değişikliklerini takip et</p>
                  </div>
                  <Switch
                    checked={settings.autoSyncInventory}
                    onCheckedChange={(v) => setSettings({...settings, autoSyncInventory: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Başarısız Webhookları Yeniden Dene</Label>
                    <p className="text-xs text-slate-500">Otomatik yeniden deneme</p>
                  </div>
                  <Switch
                    checked={settings.retryFailedWebhooks}
                    onCheckedChange={(v) => setSettings({...settings, retryFailedWebhooks: v})}
                  />
                </div>
              </div>

              {settings.retryFailedWebhooks && (
                <div className="space-y-2">
                  <Label>Maksimum Yeniden Deneme Sayısı</Label>
                  <Select 
                    value={settings.maxRetries.toString()} 
                    onValueChange={(v) => setSettings({...settings, maxRetries: parseInt(v)})}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 kez</SelectItem>
                      <SelectItem value="3">3 kez</SelectItem>
                      <SelectItem value="5">5 kez</SelectItem>
                      <SelectItem value="10">10 kez</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={saveSettings} disabled={saving}>
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Ayarları Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Webhook Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Webhook Oluştur</DialogTitle>
            <DialogDescription>
              Shopify mağazanızdan bildirim almak için bir webhook türü seçin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Webhook Türü *</Label>
              <Select 
                value={webhookForm.topic} 
                onValueChange={(v) => setWebhookForm({...webhookForm, topic: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tür seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {WEBHOOK_TOPICS.map((topic) => (
                    <SelectItem key={topic.value} value={topic.value}>
                      <div className="flex items-center gap-2">
                        <topic.icon className="w-4 h-4" />
                        {topic.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Otomatik Senkronizasyon</Label>
                <p className="text-xs text-slate-500">Bildirimi otomatik işle</p>
              </div>
              <Switch
                checked={webhookForm.autoSync}
                onCheckedChange={(v) => setWebhookForm({...webhookForm, autoSync: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>E-posta Bildirimi</Label>
                <p className="text-xs text-slate-500">Her webhook'ta e-posta gönder</p>
              </div>
              <Switch
                checked={webhookForm.notifyEmail}
                onCheckedChange={(v) => setWebhookForm({...webhookForm, notifyEmail: v})}
              />
            </div>

            {webhookForm.notifyEmail && (
              <div className="space-y-2">
                <Label>E-posta Adresi</Label>
                <Input
                  type="email"
                  value={webhookForm.email}
                  onChange={(e) => setWebhookForm({...webhookForm, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={createWebhook} disabled={saving}>
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Webhook Log Detayı</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Webhook Türü</Label>
                  <p className="font-medium">{getTopicInfo(selectedLog.topic).label}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Tarih</Label>
                  <p>{new Date(selectedLog.created_at).toLocaleString("tr-TR")}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">İşlem Süresi</Label>
                  <p>{selectedLog.processingTime || "N/A"} ms</p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Payload</Label>
                <pre className="mt-2 p-4 bg-slate-100 rounded-lg text-xs overflow-auto max-h-[300px]">
                  {JSON.stringify(selectedLog.payload || {}, null, 2)}
                </pre>
              </div>
              
              {selectedLog.error && (
                <div>
                  <Label className="text-xs text-red-500">Hata</Label>
                  <p className="mt-1 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                    {selectedLog.error}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setLogDialogOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
